import { Spinner } from '@/components/glitch-ui/spinner';
import { AlertCircle, Check } from 'lucide-react';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useCampaignEvents } from '@/hooks/useCampaignEvents';
import { type ProgressEvent, type ProgressStage } from '@/lib/apis';
import { metaCampaignEventsQueryOptions } from '@/lib/query-options';
import { cn } from '@/lib/utils';

const STAGE_LABEL: Record<ProgressStage, string> = {
  reading_page: 'Reading the page',
  understanding_business: 'Understanding the business',
  finding_angle: 'Finding the angle',
  researching_keywords: 'Researching keywords',
  writing_headlines: 'Writing headlines',
  writing_descriptions: 'Writing descriptions',
  generating_sitelinks: 'Generating site links',
  building_ad: 'Building the ad',
  setting_bid_strategy: 'Setting bid strategy',
  finalized: 'Campaign ready',
  // Meta generation stages (#5981).
  writing_ad_copy: 'Writing ad copy',
  uploading_image: 'Uploading images',
};

const FUNNEL_LABEL: Record<'upper' | 'middle' | 'lower', string> = {
  upper: 'top of funnel',
  middle: 'middle of funnel',
  lower: 'bottom of funnel',
};

interface CampaignActivityFeedProps {
  campaignId: number;
  className?: string;
}

export function CampaignActivityFeed({ campaignId, className }: CampaignActivityFeedProps) {
  const { events, isLoading } = useCampaignEvents(campaignId);
  return <ActivityFeedCard events={events} isLoading={isLoading} className={className} />;
}

/**
 * The Meta sibling (#5981): same card over the Meta events endpoint, polled every 3s while the
 * campaign is generating (Google's is push via its stream; the 3s poll matches the cadence the
 * Meta panel already uses for its progress card).
 */
export function MetaCampaignActivityFeed({
  slug,
  isGenerating,
  className,
}: {
  slug: string;
  isGenerating: boolean;
  className?: string;
}) {
  const { data: events = [], isLoading } = useQuery(metaCampaignEventsQueryOptions(slug, isGenerating));
  return <ActivityFeedCard events={events} isLoading={isLoading} className={className} />;
}

/** Presentational feed card — shared by the Google and Meta wrappers above. */
function ActivityFeedCard({
  events,
  isLoading,
  className,
}: {
  events: ProgressEvent[];
  isLoading: boolean;
  className?: string;
}) {
  const rows = useMemo(() => collapseEvents(events), [events]);
  const startTs = events[0]?.timestamp;

  if (isLoading && rows.length === 0) {
    return (
      <div className={cn('rounded-lg border bg-white p-6 shadow-sm', className)}>
        <p className="text-sm text-gray-500">Loading activity…</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={cn('rounded-lg border bg-white p-6 shadow-sm', className)}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-700 mb-4">
        Activity
      </h3>
      <ol className="space-y-3">
        {rows.map((row, idx) => (
          <FeedRow key={`${row.stage}|${row.funnel_stage ?? ''}|${row.ad_group_url ?? ''}|${idx}`}
            event={row} startTs={startTs} />
        ))}
      </ol>
    </div>
  );
}

function FeedRow({ event, startTs }: { event: ProgressEvent; startTs: string | undefined }) {
  const icon =
    event.status === 'completed' ? (
      <Check className="h-4 w-4 text-green-600" aria-label="completed" />
    ) : event.status === 'failed' ? (
      <AlertCircle className="h-4 w-4 text-red-600" aria-label="failed" />
    ) : (
      <Spinner size="sm" className="text-[#290BD9]" aria-label="in progress" />
    );

  const label = labelFor(event);
  const elapsed = formatElapsed(startTs, event.timestamp);
  const artifactLine = renderArtifacts(event);

  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p
            className={cn(
              'text-sm',
              event.status === 'failed' ? 'text-red-700' : 'text-gray-900',
            )}
          >
            {label}
          </p>
          <span className="text-xs text-gray-500 shrink-0 tabular-nums">{elapsed}</span>
        </div>
        {artifactLine && (
          <p className="mt-1 text-xs text-gray-700 break-words">{artifactLine}</p>
        )}
        {event.status === 'failed' && event.error && (
          <p className="mt-1 text-xs text-red-600 break-words">{event.error}</p>
        )}
      </div>
    </li>
  );
}

function labelFor(event: ProgressEvent): string {
  const base = STAGE_LABEL[event.stage] ?? event.stage;
  if (event.funnel_stage) {
    return `${base} for ${FUNNEL_LABEL[event.funnel_stage]}`;
  }
  return base;
}

function renderArtifacts(event: ProgressEvent): string | null {
  const a = event.artifacts;
  if (!a) return null;
  const parts: string[] = [];

  if (typeof a.angle === 'string' && a.angle) {
    parts.push(`"${a.angle}"`);
  }
  if (typeof a.summary === 'string' && a.summary) {
    parts.push(a.summary);
  }
  if (typeof a.primary_count === 'number') {
    parts.push(`${a.primary_count} primary keywords`);
  }
  if (Array.isArray(a.sample_keywords) && a.sample_keywords.length > 0) {
    parts.push(`e.g. ${(a.sample_keywords as string[]).slice(0, 3).join(', ')}`);
  }
  if (Array.isArray(a.sample_headlines) && a.sample_headlines.length > 0) {
    parts.push((a.sample_headlines as string[]).map((h) => `"${h}"`).join(' · '));
  }
  if (Array.isArray(a.sample_descriptions) && a.sample_descriptions.length > 0) {
    parts.push((a.sample_descriptions as string[]).map((d) => `"${d}"`).join(' · '));
  }
  if (typeof a.total === 'number' && a.total > 3) {
    parts.push(`+${a.total - 3} more`);
  }
  if (typeof a.headlines === 'number' && typeof a.keywords === 'number') {
    parts.push(`${a.headlines} headlines · ${a.keywords} keywords`);
  }
  if (typeof a.ads === 'number') {
    parts.push(`${a.ads} ads · ${a.headlines ?? 0} headlines · ${a.descriptions ?? 0} descriptions`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatElapsed(startIso: string | undefined, endIso: string): string {
  if (!startIso) return '';
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (isNaN(start) || isNaN(end)) return '';
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function collapseEvents(events: ProgressEvent[]): ProgressEvent[] {
  // Latest event wins per (stage, funnel_stage, ad_group_url) so COMPLETED
  // replaces STARTED while parallel ad groups keep their own row.
  const latest = new Map<string, ProgressEvent>();
  const order: string[] = [];
  for (const event of events) {
    const key = `${event.stage}|${event.funnel_stage ?? ''}|${event.ad_group_url ?? ''}`;
    if (!latest.has(key)) order.push(key);
    latest.set(key, event);
  }
  return order.map((k) => latest.get(k)!);
}
